/**
 * O diagnóstico do robô pela USB (TASK-11865): o que a placa diz na serial,
 * junto com o cadastro dela no Console, num texto para baixar. Nada vai ao
 * backend — decisão de 27/09/2026.
 *
 * Só LÊ: pergunta `INFO` e `WIFI_LIST`, e nunca manda `CONFIG` nem `WIFI `.
 * A página não alcança o `chrome://device-log`; o que ela alcança é a serial,
 * e o erro da leitura, que é o que distingue "a USB caiu" de "o robô calou".
 */
import type { RoboFrota } from '@/api/robo'
import { PRAZOS_PADRAO, identificarRobo, listarRedesSalvas, type Prazos } from './fluxo'
import type { InfoDoRobo, RedeSalva } from './protocolo'
import type { Linhas } from './serial'

export type Evento = { ms: number; tipo: 'nota' | 'lida' | 'enviada' | 'erro'; texto: string }

export type Captura = {
  eventos: Evento[]
  /** `undefined`: não respondeu; `null`: firmware sem `INFO`. */
  info: InfoDoRobo | null | undefined
  /** `undefined`: não perguntado ou sem resposta; `null`: firmware sem `WIFI_LIST`. */
  redes: RedeSalva[] | null | undefined
}

// Linha que carrega credencial ou senha. O diagnóstico não as envia e o
// firmware não as ecoa, mas o arquivo sai do Console: a redação é a defesa.
const PREFIXOS_COM_SEGREDO = ['CONFIG ', 'WIFI ']

export function redigir(linha: string): string {
  return PREFIXOS_COM_SEGREDO.some((p) => linha.startsWith(p))
    ? `${linha.split(' ')[0]} [conteúdo omitido: carrega segredo]`
    : linha
}

export function erroEmTexto(erro: unknown): string {
  if (erro && typeof erro === 'object' && 'name' in erro && 'message' in erro) {
    return `${String((erro as { name: unknown }).name)}: ${String((erro as { message: unknown }).message)}`
  }
  return String(erro)
}

// Prazos do diagnóstico: o de resposta é mais curto que o da configuração,
// porque aqui não há consulta HTTPS do firmware a esperar.
const PRAZOS_DIAGNOSTICO: Prazos = { ...PRAZOS_PADRAO, resposta: 8_000 }

export async function capturarDiagnostico(deps: {
  abrir: (aoErroDeLeitura: (erro: unknown) => void) => Promise<Linhas>
  agora?: () => number
  esperar?: (ms: number) => Promise<void>
  prazos?: Prazos
  /** Quanto tempo de log guardar, contado da abertura. */
  janelaMs?: number
}): Promise<Captura> {
  const agora = deps.agora ?? Date.now
  const esperar = deps.esperar ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))
  const prazos = deps.prazos ?? PRAZOS_DIAGNOSTICO
  const janela = deps.janelaMs ?? 20_000
  const t0 = agora()
  const eventos: Evento[] = []
  const registrar = (tipo: Evento['tipo'], texto: string) =>
    eventos.push({ ms: agora() - t0, tipo, texto })
  let caiu = false

  registrar('nota', 'abrindo a porta (a placa reinicia)')
  let porta: Linhas
  try {
    porta = await deps.abrir((erro) => {
      caiu = true
      registrar('erro', `leitura interrompida: ${erroEmTexto(erro)}`)
    })
  } catch (erro) {
    registrar('erro', `a porta não abriu: ${erroEmTexto(erro)}`)
    return { eventos, info: undefined, redes: undefined }
  }

  // Tudo o que passa pela porta entra na linha do tempo, na ordem.
  const linhas: Linhas = {
    async proxima(prazoMs) {
      const l = await porta.proxima(prazoMs)
      if (l !== null) registrar('lida', redigir(l))
      return l
    },
    async escrever(linha) {
      registrar('enviada', redigir(linha))
      await porta.escrever(linha)
    },
    fechar: () => porta.fechar(),
  }

  let info: Captura['info']
  let redes: Captura['redes']
  try {
    const r = await identificarRobo(linhas, prazos, agora, esperar,
      () => registrar('nota', 'fim do boot (ou prazo do boot vencido)'))
    info = r?.info
    if (r === undefined) registrar('nota', `sem resposta ao INFO em ${prazos.resposta} ms`)
    if (info) {
      redes = await listarRedesSalvas(linhas, prazos, agora)
      if (redes === undefined) registrar('nota', `sem resposta ao WIFI_LIST em ${prazos.resposta} ms`)
    }
    // O resto da janela: o que a placa imprime sozinha (tentativas de Wi-Fi,
    // estado do console) também é diagnóstico.
    while (!caiu && agora() - t0 < janela) {
      if (await linhas.proxima(janela - (agora() - t0)) === null) break
    }
  } catch (erro) {
    registrar('erro', `a captura parou: ${erroEmTexto(erro)}`)
  } finally {
    await linhas.fechar().catch(() => {})
    registrar('nota', 'porta fechada')
  }
  return { eventos, info, redes }
}

export type ContextoDoRelatorio = {
  robo: RoboFrota
  versaoConsole: string
  navegador: string
  portaUsb: { usbVendorId?: number; usbProductId?: number } | null
  geradoEm: Date
}

const hex = (n: number | undefined) => (n === undefined ? '?' : `0x${n.toString(16).padStart(4, '0')}`)
const ou = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '—' : String(v))

function compararVersao(naPlaca: string, noCadastro: string | null): string {
  if (!noCadastro) return `a placa roda ${naPlaca}; o Console ainda não recebeu versão deste robô`
  return naPlaca === noCadastro ? `iguais (${naPlaca})` : `DIVERGEM: placa ${naPlaca}, Console ${noCadastro}`
}

export function montarRelatorio(c: Captura, ctx: ContextoDoRelatorio): string {
  const r = ctx.robo
  const validade = r.token_expires_at ? new Date(r.token_expires_at * 1000).toISOString() : null
  const respostaInfo = c.info === undefined ? 'sem resposta'
    : c.info === null ? 'firmware sem INFO'
      : JSON.stringify(c.info)
  const respostaRedes = c.redes === undefined ? 'não obtida'
    : c.redes === null ? 'firmware sem WIFI_LIST'
      : c.redes.length === 0 ? 'nenhuma rede salva'
        : c.redes.map((w) => `${w.ssid}${w.em_uso ? ' (em uso)' : ''}${w.confirmada ? '' : ' (não confirmada)'}`).join(', ')
  const erros = c.eventos.filter((e) => e.tipo === 'erro')
  const marca = { nota: '--', lida: '<', enviada: '>', erro: '!!' } as const

  return [
    'DIAGNÓSTICO DO ROBÔ PELA USB — ConsoleAI',
    `Gerado em: ${ctx.geradoEm.toISOString()}`,
    '',
    '== Cadastro no Console',
    `Identificador: ${r.public_id}`,
    `Nome: ${ou(r.name)}`,
    `Modelo: ${ou(r.hardware_model)}`,
    `Status: ${r.status}`,
    `MAC cadastrado: ${ou(r.mac)}`,
    `Firmware registrado: ${r.firmware_version ?? 'o robô ainda não anunciou versão ao Console'}`,
    // Sem versão anunciada o veredito é o default de `classificar`
    // ('anterior', sem `ultima`): imprimir cru parecia catálogo vazio (TASK-11869).
    ...(r.firmware_version
      ? [`Firmware do catálogo: ${ou(r.firmware.ultima)} (estado: ${r.firmware.estado})`]
      : []),
    `Última comunicação: ${ou(r.last_seen_at)}`,
    `Credencial: ${r.tem_credencial ? `emitida, vence ${ou(validade)}` : 'não emitida'}`,
    '',
    '== Ambiente',
    `Console: v${ctx.versaoConsole}`,
    `Navegador: ${ctx.navegador}`,
    `Porta USB: ${ctx.portaUsb ? `vendor ${hex(ctx.portaUsb.usbVendorId)} produto ${hex(ctx.portaUsb.usbProductId)}` : '—'}`,
    '',
    '== O que a placa respondeu',
    `INFO: ${respostaInfo}`,
    ...(c.info ? [`Versão na placa x cadastro: ${compararVersao(c.info.versao, r.firmware_version)}`] : []),
    `Redes salvas: ${respostaRedes}`,
    `Erros da porta: ${erros.length ? erros.map((e) => e.texto).join(' | ') : 'nenhum'}`,
    '',
    '== Linha do tempo (ms desde a abertura; < lida, > enviada, !! erro)',
    ...c.eventos.map((e) => `${String(e.ms).padStart(6)} ${marca[e.tipo]} ${e.texto}`),
    '',
  ].join('\n')
}
